/**
 * A BARRA DA DIREITA — o painel da loja em foco.
 *
 * O QUE ERA: 1084 linhas com a rede inteira escrita à mão ("Alfredo Foods
 * Corp", "Loja Number One - Centro", "Pizzaria Alfredo - Vila Madalena"),
 * `updateMetrics` somando `Math.random() * 100` às vendas a cada 8 segundos,
 * `generateActivityLogs` inventando "Venda #1234 - R$ 450,00 - Cartão" e
 * "Novo pedido iFood #5678", e dois alertas fixos ("PDV 2 desconectado",
 * "Sincronização em andamento") que nunca vieram de sistema nenhum.
 *
 * O QUE É AGORA: a mesma rede que a Home usa, vinda de `GET /painel/rede` pelo
 * `useRedeStore` — uma requisição por sessão, e a unidade em foco é a MESMA nos
 * dois lados. Escolher uma loja aqui muda a Home, e vice-versa.
 *
 * AS TRÊS ABAS, e o que cada uma pode honestamente mostrar hoje:
 *
 *   Visão      cadastro real da unidade + ações que funcionam (telefone real)
 *   Métricas   o número do período, e o total da rede ao lado
 *   Atividades NÃO EXISTE feed de eventos no backend. A aba diz isso e mostra
 *              os AVISOS medidos que o endpoint devolve — que são fatos sobre
 *              o dado, não enfeite. Inventar um feed aqui seria repetir
 *              exatamente o que esta reescrita veio desfazer.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAbasAcessiveis } from "../../hooks/useAbasAcessiveis";
import * as AlfredoStyles from "../../styles/layouts/layouts";
import * as Estilos from "../../styles/layouts/RightBar";
import * as AlfredoInterfaces from "../../interfaces";
import { useRedeStore } from "../../store";
import { comoDinheiro, type UnidadeDoPainel } from "../../services/painel.service";

type Aba = "visao" | "metricas" | "atividades";
type Estado = "online" | "warning" | "offline";

function estadoDaUnidade(unidade: UnidadeDoPainel | null): Estado {
  if (!unidade || !unidade.operaNoOps) return "offline";
  return unidade.aceitaPedido ? "online" : "warning";
}

function rotuloDoEstado(estado: Estado): string {
  if (estado === "online") return "Operando";
  if (estado === "warning") return "Sem pedido online";
  return "Sem operação no Alfredo";
}

/** As três abas da barra, na ordem em que aparecem. */
const ABAS = [
  { chave: "visao" as const, icone: "dashboard", rotulo: "Visão" },
  { chave: "metricas" as const, icone: "insights", rotulo: "Métricas" },
  { chave: "atividades" as const, icone: "notifications", rotulo: "Avisos" },
];

const RightBar: React.FC<AlfredoInterfaces.SidebarProps> = ({ isOpen }) => {
  const {
    painel,
    carregando,
    erro,
    unidadeSelecionada,
    selecionarUnidade,
    carregarRede,
  } = useRedeStore();

  const [aba, setAba] = useState<Aba>("visao");

  const { propsDaFaixa, propsDaAba } = useAbasAcessiveis({
    itens: ABAS.map((a) => a.chave),
    selecionada: aba,
    aoSelecionar: setAba,
    orientacao: "horizontal",
    rotulo: "Painel da unidade",
  });
  const [mostrarLista, setMostrarLista] = useState(false);

  useEffect(() => {
    if (!painel && !carregando && !erro) void carregarRede();
  }, [painel, carregando, erro, carregarRede]);

  const unidades = useMemo(
    () => painel?.companies.flatMap((c) => c.unidades) ?? [],
    [painel],
  );

  const unidade = useMemo(
    () => unidades.find((u) => u.uuid === unidadeSelecionada) ?? null,
    [unidades, unidadeSelecionada],
  );

  const estado = estadoDaUnidade(unidade);

  const ligar = () => {
    if (!unidade?.telefone) return;
    window.open(`tel:${unidade.telefone.replace(/\D/g, "")}`, "_self");
  };

  const abrirWhatsapp = () => {
    const numero = unidade?.whatsapp || unidade?.telefone;
    if (!numero) return;
    window.open(
      `https://wa.me/55${numero.replace(/\D/g, "")}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <AlfredoStyles.RightSidebarContainer isOpen={isOpen}>
      <Estilos.Cabecalho>
        <Estilos.LinhaDoTitulo>
          <Estilos.Titulo title={unidade?.nome ?? "Rede"}>
            {unidade?.nome ?? painel?.organizacao?.nome ?? "Rede"}
          </Estilos.Titulo>
          <Estilos.BotaoDoCabecalho
            type="button"
            aria-expanded={mostrarLista}
            aria-label="Escolher unidade"
            onClick={() => setMostrarLista((antes) => !antes)}
          >
            <span className="material-icons">
              {mostrarLista ? "close" : "store"}
            </span>
          </Estilos.BotaoDoCabecalho>
        </Estilos.LinhaDoTitulo>

        {unidade && (
          <Estilos.LinhaDaOrganizacao>
            <Estilos.Bolinha $estado={estado} />
            <span>
              {painel?.organizacao?.nome} · {unidade.companyNome}
            </span>
          </Estilos.LinhaDaOrganizacao>
        )}
      </Estilos.Cabecalho>

      {mostrarLista && painel && (
        <Estilos.ListaDeUnidades>
          {painel.companies.map((company) => (
            <div key={company.uuid}>
              <Estilos.GrupoDaCompany>{company.nome}</Estilos.GrupoDaCompany>
              {company.unidades.map((item) => (
                <Estilos.ItemDeUnidade
                  key={item.uuid}
                  type="button"
                  $selecionada={item.uuid === unidadeSelecionada}
                  onClick={() => {
                    selecionarUnidade(item.uuid);
                    setMostrarLista(false);
                  }}
                >
                  <Estilos.Bolinha $estado={estadoDaUnidade(item)} />
                  <div>
                    <Estilos.NomeDaUnidade>{item.nome}</Estilos.NomeDaUnidade>
                    <Estilos.DetalheDaUnidade>
                      {item.endereco?.split(",")[0] ?? "Endereço não cadastrado"}
                    </Estilos.DetalheDaUnidade>
                  </div>
                </Estilos.ItemDeUnidade>
              ))}
            </div>
          ))}
        </Estilos.ListaDeUnidades>
      )}

      {/*
        As três abas já eram <button> com role e aria-selected — o que faltava
        era o RESTO do padrão: a faixa sem nome (o leitor anunciava "grupo de
        abas", sem dizer de quê), sem orientação, e o Tab parando em cada uma
        das três antes de deixar a pessoa chegar ao conteúdo. O mesmo hook das
        abas da Configuração resolve os três de uma vez.
      */}
      <Estilos.Abas {...propsDaFaixa}>
        {ABAS.map(({ chave, icone, rotulo }) => (
          <Estilos.Aba
            key={chave}
            type="button"
            $ativa={aba === chave}
            {...propsDaAba(chave)}
            onClick={() => setAba(chave)}
          >
            <span className="material-icons">{icone}</span>
            {rotulo}
          </Estilos.Aba>
        ))}
      </Estilos.Abas>

      {carregando && <Estilos.Vazio>Carregando a rede…</Estilos.Vazio>}

      {!carregando && erro && <Estilos.Vazio role="alert">{erro}</Estilos.Vazio>}

      {!carregando && !erro && !unidade && (
        <Estilos.Vazio>Nenhuma unidade cadastrada nesta organização.</Estilos.Vazio>
      )}

      {!carregando && !erro && unidade && (
        <Estilos.Corpo>
          {aba === "visao" && (
            <>
              <Estilos.Cartao>
                <Estilos.TituloDoCartao>
                  {rotuloDoEstado(estado)}
                </Estilos.TituloDoCartao>
                <Estilos.Endereco>
                  {unidade.endereco ?? "Endereço não cadastrado"}
                </Estilos.Endereco>
                <Estilos.Acoes>
                  <Estilos.BotaoDeAcao
                    type="button"
                    disabled={!unidade.telefone}
                    onClick={ligar}
                  >
                    <span className="material-icons">call</span>
                    {unidade.telefone ?? "Sem telefone"}
                  </Estilos.BotaoDeAcao>
                  <Estilos.BotaoDeAcao
                    type="button"
                    $whatsapp
                    disabled={!(unidade.whatsapp || unidade.telefone)}
                    onClick={abrirWhatsapp}
                  >
                    <span className="material-icons">chat</span>
                    WhatsApp
                  </Estilos.BotaoDeAcao>
                </Estilos.Acoes>
              </Estilos.Cartao>

              <Estilos.Cartao>
                <Estilos.TituloDoCartao>Cadastro</Estilos.TituloDoCartao>
                <Estilos.GradeDeNumeros>
                  <Estilos.Numero>
                    <Estilos.RotuloDoNumero>Pedido online</Estilos.RotuloDoNumero>
                    <Estilos.ValorDoNumero
                      $tom={unidade.aceitaPedido ? "verde" : "ambar"}
                    >
                      {unidade.aceitaPedido ? "Ligado" : "Desligado"}
                    </Estilos.ValorDoNumero>
                  </Estilos.Numero>
                  <Estilos.Numero>
                    <Estilos.RotuloDoNumero>Pino no mapa</Estilos.RotuloDoNumero>
                    <Estilos.ValorDoNumero
                      $tom={unidade.lat == null ? "ambar" : "verde"}
                    >
                      {unidade.lat == null
                        ? "Sem coordenada"
                        : unidade.coordenadaConferida
                        ? "Conferido"
                        : "A conferir"}
                    </Estilos.ValorDoNumero>
                  </Estilos.Numero>
                </Estilos.GradeDeNumeros>
              </Estilos.Cartao>
            </>
          )}

          {aba === "metricas" && (
            <>
              <Estilos.Cartao>
                <Estilos.TituloDoCartao>
                  {unidade.nome} · {painel?.periodo.de}
                  {painel && painel.periodo.ate !== painel.periodo.de
                    ? ` a ${painel.periodo.ate}`
                    : ""}
                </Estilos.TituloDoCartao>

                {unidade.numeros ? (
                  <Estilos.GradeDeNumeros>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Receita líquida</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="verde">
                        {comoDinheiro(unidade.numeros.receitaLiquida)}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Pedidos</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="azul">
                        {unidade.numeros.pedidos}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Pessoas</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="azul">
                        {unidade.numeros.pessoas}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Ticket</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="ambar">
                        {unidade.numeros.ticket
                          ? comoDinheiro(unidade.numeros.ticket)
                          : "—"}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                  </Estilos.GradeDeNumeros>
                ) : (
                  <Estilos.Vazio>
                    Esta unidade não tem operação no Alfredo: não há caixa nem
                    pedido para medir. Zero seria mentira.
                  </Estilos.Vazio>
                )}
              </Estilos.Cartao>

              {painel && (
                <Estilos.Cartao>
                  <Estilos.TituloDoCartao>
                    Rede inteira no período
                  </Estilos.TituloDoCartao>
                  <Estilos.GradeDeNumeros>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Receita líquida</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="verde">
                        {comoDinheiro(painel.totalDaRede.receitaLiquida)}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                    <Estilos.Numero>
                      <Estilos.RotuloDoNumero>Pedidos</Estilos.RotuloDoNumero>
                      <Estilos.ValorDoNumero $tom="azul">
                        {painel.totalDaRede.pedidos}
                      </Estilos.ValorDoNumero>
                    </Estilos.Numero>
                  </Estilos.GradeDeNumeros>
                </Estilos.Cartao>
              )}
            </>
          )}

          {aba === "atividades" && (
            <>
              <Estilos.Cartao>
                <Estilos.TituloDoCartao>
                  Não há registro de atividade
                </Estilos.TituloDoCartao>
                <Estilos.Endereco>
                  O Alfredo ainda não guarda um histórico de eventos por loja
                  (venda registrada, PDV que caiu, pedido do iFood). Enquanto
                  não houver, esta aba mostra o que É medido: os avisos que o
                  próprio painel apurou sobre o dado.
                </Estilos.Endereco>
              </Estilos.Cartao>

              {(painel?.avisos ?? []).map((aviso) => (
                <Estilos.Aviso
                  key={aviso.codigo}
                  $tom={
                    aviso.codigo.startsWith("UNIDADE_SEM") ? "alerta" : "info"
                  }
                >
                  <span className="material-icons">
                    {aviso.codigo.startsWith("UNIDADE_SEM")
                      ? "error_outline"
                      : "info"}
                  </span>
                  <div>
                    <Estilos.CodigoDoAviso>{aviso.codigo}</Estilos.CodigoDoAviso>
                    {aviso.texto}
                  </div>
                </Estilos.Aviso>
              ))}
            </>
          )}
        </Estilos.Corpo>
      )}

      {painel && (
        <Estilos.Rodape>
          Cadastro: {painel.fonte.cadastro}
          <br />
          Operação: {painel.fonte.operacao}
        </Estilos.Rodape>
      )}
    </AlfredoStyles.RightSidebarContainer>
  );
};

export default RightBar;
